/**
 * useWebRTC — manage a single WebRTC peer connection between the two
 * players in a match. Signaling rides on the existing match WS via
 * sendWebRTCSignal / onWebRTCSignal callbacks.
 *
 * The "polite peer" pattern resolves offer collisions: peer with the
 * lexically smaller PlayerId is impolite (initiates offers); the other
 * is polite (rolls back local offer on collision).
 *
 * TURN credentials fetched from /api/turn-credentials at hook start.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiPath } from '../../api.js';

type Status = 'idle' | 'connecting' | 'connected' | 'failed' | 'closed';

type Signal =
  | { type: 'offer'; sdp: RTCSessionDescriptionInit }
  | { type: 'answer'; sdp: RTCSessionDescriptionInit }
  | { type: 'ice'; candidate: RTCIceCandidateInit };

type IceServerConfig = {
  urls: string | readonly string[];
  username?: string;
  credential?: string;
};

export type WebRTCHook = {
  status: Status;
  /** Local mic mute toggle. */
  muted: boolean;
  setMuted: (muted: boolean) => void;
  /** Peer audio level (0..1) — sampled from analyzer for VU meter. */
  peerLevel: number;
  /** Volume control on the peer's audio (0..1). */
  peerVolume: number;
  setPeerVolume: (v: number) => void;
  /** Connection-quality hint based on ICE state + RTC stats. */
  quality: 'excellent' | 'good' | 'poor' | 'unknown';
  /** Dispatch an inbound signal arriving over the WS. */
  receiveSignal: (payload: unknown) => void;
};

type Params = {
  viewer: string;
  peer: string;
  /** Send an outbound signal payload via the match WS. */
  sendSignal: (payload: unknown) => void;
};

export function useWebRTC({ viewer, peer, sendSignal }: Params): WebRTCHook {
  const [status, setStatus] = useState<Status>('idle');
  const [muted, setMutedState] = useState(true); // start muted
  const [peerLevel, setPeerLevel] = useState(0);
  const [peerVolume, setPeerVolumeState] = useState(0.8);
  const [quality, setQuality] = useState<WebRTCHook['quality']>('unknown');

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const makingOfferRef = useRef(false);
  const ignoreOfferRef = useRef(false);
  const isPolite = viewer > peer; // lexical compare

  // Track the latest setSignal / muted / peerVolume values via refs so
  // the setup useEffect doesn't have to re-run when they change.
  const sendSignalRef = useRef(sendSignal);
  sendSignalRef.current = sendSignal;
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const peerVolumeRef = useRef(peerVolume);
  peerVolumeRef.current = peerVolume;

  // ─── Setup ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!peer || !viewer) {
      setStatus('idle');
      return;
    }
    let cancelled = false;

    const setup = async () => {
      setStatus('connecting');
      let iceServers: IceServerConfig[] = [{ urls: 'stun:stun.cloudflare.com:3478' }];
      try {
        const res = await fetch(apiPath('/api/turn-credentials'), { credentials: 'include' });
        if (res.ok) {
          const json = (await res.json()) as { iceServers?: IceServerConfig[] };
          if (Array.isArray(json.iceServers)) iceServers = json.iceServers;
        }
      } catch {
        // fall back to STUN only
      }
      if (cancelled) return;

      const pc = new RTCPeerConnection({ iceServers: iceServers as RTCIceServer[] });
      pcRef.current = pc;

      // Pre-create the audio sender so subsequent track changes are
      // renegotiation-free.
      const transceiver = pc.addTransceiver('audio', { direction: 'sendrecv' });

      pc.addEventListener('icecandidate', (ev) => {
        if (!ev.candidate) return;
        sendSignalRef.current({ type: 'ice', candidate: ev.candidate.toJSON() } satisfies Signal);
      });

      pc.addEventListener('iceconnectionstatechange', () => {
        const s = pc.iceConnectionState;
        if (s === 'connected' || s === 'completed') {
          setStatus('connected');
          setQuality('good');
        } else if (s === 'failed') {
          setStatus('failed');
          setQuality('poor');
        } else if (s === 'disconnected') {
          setQuality('poor');
        }
      });

      pc.addEventListener('track', (ev) => {
        const [stream] = ev.streams;
        if (!stream) return;
        if (!remoteAudioRef.current) {
          const audio = document.createElement('audio');
          audio.autoplay = true;
          audio.volume = peerVolumeRef.current;
          document.body.appendChild(audio);
          remoteAudioRef.current = audio;
        }
        remoteAudioRef.current.srcObject = stream;

        // VU meter (decorative — best-effort).
        try {
          const audioCtx = new AudioContext();
          const src = audioCtx.createMediaStreamSource(stream);
          const analyser = audioCtx.createAnalyser();
          analyser.fftSize = 256;
          src.connect(analyser);
          const buf = new Uint8Array(analyser.frequencyBinCount);
          const loop = () => {
            if (!pcRef.current) return;
            analyser.getByteFrequencyData(buf);
            const sum = buf.reduce((a, b) => a + b, 0);
            setPeerLevel(Math.min(1, sum / buf.length / 128));
            requestAnimationFrame(loop);
          };
          loop();
        } catch {
          // ignore — meter is decorative
        }
      });

      pc.addEventListener('negotiationneeded', async () => {
        try {
          makingOfferRef.current = true;
          await pc.setLocalDescription();
          if (!pc.localDescription) return;
          sendSignalRef.current({
            type: 'offer',
            sdp: pc.localDescription.toJSON(),
          } satisfies Signal);
        } finally {
          makingOfferRef.current = false;
        }
      });

      // Acquire mic. If the user denies permission we proceed in
      // listen-only mode — the peer can still hear us be silent.
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        });
        localStreamRef.current = stream;
        const track = stream.getAudioTracks()[0];
        if (track) {
          track.enabled = !mutedRef.current;
          transceiver.sender.replaceTrack(track);
        }
      } catch {
        // mic access denied — connection still useful for receiving
      }
    };

    void setup();

    return () => {
      cancelled = true;
      pcRef.current?.close();
      pcRef.current = null;
      const local = localStreamRef.current;
      if (local) {
        for (const t of local.getTracks()) {
          t.stop();
        }
      }
      localStreamRef.current = null;
      if (remoteAudioRef.current) {
        remoteAudioRef.current.remove();
        remoteAudioRef.current = null;
      }
      setStatus('closed');
    };
  }, [viewer, peer]);

  // Mute toggle
  const setMuted = useCallback((next: boolean) => {
    setMutedState(next);
    const stream = localStreamRef.current;
    stream?.getAudioTracks().forEach((t) => {
      t.enabled = !next;
    });
  }, []);

  // Peer volume control
  const setPeerVolume = useCallback((next: number) => {
    setPeerVolumeState(next);
    if (remoteAudioRef.current) remoteAudioRef.current.volume = next;
  }, []);

  // Inbound signal handler — perfect-negotiation pattern
  const receiveSignal = useCallback(
    async (payload: unknown) => {
      const pc = pcRef.current;
      if (!pc) return;
      const sig = payload as Signal;
      try {
        if (sig.type === 'offer' || sig.type === 'answer') {
          const collision =
            sig.type === 'offer' && (makingOfferRef.current || pc.signalingState !== 'stable');
          ignoreOfferRef.current = !isPolite && collision;
          if (ignoreOfferRef.current) return;
          await pc.setRemoteDescription(sig.sdp);
          if (sig.type === 'offer') {
            await pc.setLocalDescription();
            if (pc.localDescription) {
              sendSignal({
                type: 'answer',
                sdp: pc.localDescription.toJSON(),
              } satisfies Signal);
            }
          }
        } else if (sig.type === 'ice') {
          try {
            await pc.addIceCandidate(sig.candidate);
          } catch (err) {
            if (!ignoreOfferRef.current) throw err;
          }
        }
      } catch (err) {
        // Swallow signaling errors — auto-recovery on next negotiation cycle.
        console.warn('webrtc receiveSignal error', err);
      }
    },
    [isPolite, sendSignal],
  );

  return {
    status,
    muted,
    setMuted,
    peerLevel,
    peerVolume,
    setPeerVolume,
    quality,
    receiveSignal,
  };
}
